import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Trash2 } from 'lucide-react';
import api from '@/lib/api';

interface Suggestion {
  suggestionId: string;
  userId: string;
  title: string;
  description: string;
  status: string;
  adminNotes: string | null;
  createdAt: string;
  updatedAt: string;
}

const STATUS_OPTIONS = [
  { value: 'new', label: 'New', color: 'bg-blue-100 text-blue-800 border-blue-200' },
  {
    value: 'reviewed',
    label: 'Reviewed',
    color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  },
  { value: 'planned', label: 'Planned', color: 'bg-purple-100 text-purple-800 border-purple-200' },
  { value: 'archived', label: 'Archived', color: 'bg-gray-100 text-gray-800 border-gray-200' },
];

const FILTER_OPTIONS = [{ value: 'all', label: 'All' }, ...STATUS_OPTIONS];

function StatusBadge({ status }: { status: string }) {
  const config = STATUS_OPTIONS.find((s) => s.value === status) || STATUS_OPTIONS[0];
  return (
    <Badge variant="outline" className={config.color}>
      {config.label}
    </Badge>
  );
}

export function AdminSuggestionsPage() {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');

  // Edit dialog
  const [editSuggestion, setEditSuggestion] = useState<Suggestion | null>(null);
  const [editStatus, setEditStatus] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [saving, setSaving] = useState(false);

  // Delete confirmation
  const [deleteSuggestion, setDeleteSuggestion] = useState<Suggestion | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchSuggestions = useCallback(async () => {
    setLoading(true);
    try {
      const result = await api.listSuggestionsAdmin({
        status: statusFilter === 'all' ? undefined : statusFilter,
        limit: 50,
      });
      setSuggestions(result.suggestions);
      setTotal(result.total);
    } catch (err) {
      console.error('Failed to load suggestions:', err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchSuggestions();
  }, [fetchSuggestions]);

  const handleEdit = (suggestion: Suggestion) => {
    setEditSuggestion(suggestion);
    setEditStatus(suggestion.status);
    setEditNotes(suggestion.adminNotes || '');
  };

  const handleSave = async () => {
    if (!editSuggestion) return;
    setSaving(true);
    try {
      await api.updateSuggestionAdmin(editSuggestion.suggestionId, {
        status: editStatus,
        adminNotes: editNotes.trim() || null,
      });
      setEditSuggestion(null);
      await fetchSuggestions();
    } catch (err) {
      console.error('Failed to update suggestion:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteSuggestion) return;
    setDeleting(true);
    try {
      await api.deleteSuggestionAdmin(deleteSuggestion.suggestionId);
      setDeleteSuggestion(null);
      await fetchSuggestions();
    } catch (err) {
      console.error('Failed to delete suggestion:', err);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Feature Suggestions</h2>
          <p className="text-sm text-muted-foreground">
            {total} suggestion{total !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {/* Status filter */}
      <div className="flex gap-2 flex-wrap">
        {FILTER_OPTIONS.map((opt) => (
          <Button
            key={opt.value}
            variant={statusFilter === opt.value ? 'default' : 'outline'}
            size="sm"
            onClick={() => setStatusFilter(opt.value)}
          >
            {opt.label}
          </Button>
        ))}
      </div>

      {/* Table */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-12 rounded-lg" />
          ))}
        </div>
      ) : suggestions.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8">No suggestions found.</p>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead className="w-[120px]">Status</TableHead>
                <TableHead className="w-[100px]">Date</TableHead>
                <TableHead className="w-[80px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {suggestions.map((s) => (
                <TableRow
                  key={s.suggestionId}
                  className="cursor-pointer"
                  onClick={() => handleEdit(s)}
                >
                  <TableCell>
                    <p className="font-medium text-sm truncate max-w-[400px]">{s.title}</p>
                    <p className="text-xs text-muted-foreground line-clamp-1 max-w-[400px]">
                      {s.description}
                    </p>
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={s.status} />
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {new Date(s.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteSuggestion(s);
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Edit dialog */}
      <Dialog open={!!editSuggestion} onOpenChange={(open) => !open && setEditSuggestion(null)}>
        <DialogContent>
          {editSuggestion && (
            <>
              <DialogHeader>
                <DialogTitle>{editSuggestion.title}</DialogTitle>
              </DialogHeader>
              <div className="rounded-md bg-muted p-3 max-h-[200px] overflow-y-auto">
                <p className="text-sm whitespace-pre-wrap">{editSuggestion.description}</p>
              </div>
              {editSuggestion.adminNotes && (
                <div className="rounded-md bg-muted/50 p-3">
                  <p className="text-xs font-medium text-muted-foreground mb-1">
                    Previous admin notes:
                  </p>
                  <p className="text-sm whitespace-pre-wrap">{editSuggestion.adminNotes}</p>
                </div>
              )}
              <div className="space-y-4 py-2">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Status</label>
                  <Select value={editStatus} onValueChange={setEditStatus}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Admin Notes</label>
                  <Textarea
                    value={editNotes}
                    onChange={(e) => setEditNotes(e.target.value)}
                    placeholder="Internal notes about this suggestion..."
                    rows={3}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditSuggestion(null)}>
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteSuggestion} onOpenChange={(open) => !open && setDeleteSuggestion(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Suggestion</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete &ldquo;{deleteSuggestion?.title}&rdquo;? This cannot
              be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteSuggestion(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
